import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField, Button, Container, Typography, Alert, CircularProgress,
  InputAdornment, IconButton, Snackbar, FormControlLabel, Checkbox,
  Box,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import axios from '../services/api';
import UserAgreement from '../components/tenantcomponents/LegalAgreements/UserAgreement';

const RegistrationPage = () => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        // system_role removed – it's now always 'system_user' on the backend
        accepted_user_agreement: false,
    });
    const [avatar, setAvatar] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleFileChange = (e) => setAvatar(e.target.files[0]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess(false);

        if (!formData.accepted_user_agreement) {
            setError('You must accept the User Responsibility Agreement');
            setLoading(false);
            return;
        }

        try {
            const formPayload = new FormData();
            formPayload.append('username', formData.username);
            formPayload.append('email', formData.email);
            formPayload.append('password', formData.password);
            // system_role is NOT sent – backend defaults to 'system_user'
            formPayload.append('accepted_user_agreement', formData.accepted_user_agreement);
            if (avatar) formPayload.append('avatar', avatar);

            const response = await axios.post('/register/', formPayload);

            if (response.status >= 200 && response.status < 300) {
                setSuccess(true);
                setTimeout(() => navigate('/login'), 2000);
            }
        } catch (err) {
            const data = err.response?.data;
            let message = 'Registration failed. Please try again.';
            if (data) {
                if (typeof data === 'string') {
                    message = data;
                } else if (data.detail) {
                    message = data.detail;
                } else {
                    const firstKey = Object.keys(data)[0];
                    if (firstKey && Array.isArray(data[firstKey])) {
                        message = `${firstKey}: ${data[firstKey][0]}`;
                    }
                }
            }
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="xs" sx={{ mt: 8 }}>
            <Typography variant="h4" gutterBottom align="center">
                Create Account
            </Typography>

            <Snackbar
                open={success}
                autoHideDuration={2000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="success" sx={{ width: '100%' }}>
                    Registration successful! Redirecting to login...
                </Alert>
            </Snackbar>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <form onSubmit={handleSubmit}>
                <TextField
                    label="Username" fullWidth margin="normal" required
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                />
                <TextField
                    label="Email" type="email" fullWidth margin="normal" required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
                <TextField
                    label="Password" type="password" fullWidth margin="normal" required
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                />

                {/* ── Role dropdown removed ── */}
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    Your account will be reviewed and activated by an administrator.
                    Please contact your pharmacy manager after registering.
                </Typography>

                <TextField
                    label="Profile Picture" type="file" fullWidth margin="normal"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton component="span"><CloudUpload /></IconButton>
                            </InputAdornment>
                        ),
                    }}
                    inputProps={{ accept: "image/*", onChange: handleFileChange }}
                />

                <Box sx={{ mt: 2, mb: 2 }}>
                    <UserAgreement />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.accepted_user_agreement}
                                onChange={(e) => setFormData({...formData, accepted_user_agreement: e.target.checked})}
                                required
                            />
                        }
                        label="I have read and accept the User Responsibility Agreement"
                    />
                </Box>

                <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : 'Register'}
                </Button>
            </form>

            <Typography variant="body2" sx={{ mt: 2 }}>
                Already have an account?
                <Button onClick={() => navigate('/login')} sx={{ ml: 1, textTransform: 'none' }}>
                    Login here
                </Button>
            </Typography>
        </Container>
    );
};

export default RegistrationPage;